import {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'
import {AuthContext} from '../../../contexts/auth'
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
    DadosInexigibilidade,
    InexigibilidadeApiService,
    ReferenciaInex,
} from '../../../services/InexigibilidadeApi.service'
import {getCurrentColors} from '../../../utils/colors'

const COLORS = getCurrentColors()
const ACCENT = COLORS?.primariaEscura || '#3394EF'

const HIPOTESES = [
    'Art. 74, I — fornecedor exclusivo (com atestado de exclusividade)',
    'Art. 74, II — serviços técnicos especializados de natureza singular, com profissionais de notória especialização',
    'Art. 74, III — profissional do setor artístico consagrado pela crítica/opinião pública',
    'Art. 74, IV — credenciamento',
    'Art. 74, V — aquisição/locação de imóvel cujas características o tornem o único atendente da necessidade',
    'Outra',
]

const Container = styled.div`
    flex: 1;
    height: 100vh;
    overflow-y: auto;
    padding: 28px 40px;
    background-color: #f6f8fa;
    @media (max-width: 640px) {
        padding: 18px 14px;
    }
`
const Card = styled.div`
    background: #fff;
    border: 1px solid #e6e9ee;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 22px;
`
const SectionTitle = styled.h3`
    font-size: 17px;
    font-weight: 700;
    color: #1f2b3a;
    margin: 0 0 16px 0;
`
const FieldLabel = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #3a3a3a;
    margin: 10px 0 6px 2px;
`
const Th = styled.th`
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: #7a8896;
    text-transform: uppercase;
    padding: 8px 10px;
    border-bottom: 1px solid #eef1f4;
`
const Td = styled.td`
    padding: 8px 10px;
    border-bottom: 1px solid #f1f3f6;
    vertical-align: top;
`

const brl = (v?: number | null) =>
    v == null || !Number.isFinite(Number(v))
        ? '—'
        : new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(v))

function parecer(refs: ReferenciaInex[], preco: number) {
    const vals = refs
        .map((r) => Number(r.valor))
        .filter((v) => Number.isFinite(v) && v > 0)
        .sort((a, b) => a - b)
    const n = vals.length
    if (!n || !Number.isFinite(preco) || preco <= 0)
        return {cor: '#64748b', texto: 'Informe o preço proposto e ao menos uma referência para a análise de compatibilidade.'}
    const min = vals[0]
    const max = vals[n - 1]
    if (preco > max) return {cor: '#b45309', texto: `O preço proposto (${brl(preco)}) está ACIMA da maior referência praticada (${brl(max)}). Recomenda-se justificativa adicional.`}
    if (preco < min) return {cor: '#15803d', texto: `O preço proposto (${brl(preco)}) está ABAIXO da menor referência (${brl(min)}) — favorável à Administração.`}
    return {cor: '#15803d', texto: `O preço proposto (${brl(preco)}) é COMPATÍVEL com a faixa praticada (${brl(min)} a ${brl(max)}).`}
}

export default function Inexigibilidade() {
    const api = useMemo(() => new InexigibilidadeApiService(), [])
    const {userInfo} = useContext(AuthContext)
    const nomeUsuario = `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim()
    const orgaoUsuario = userInfo?.organization?.name || ''
    const [view, setView] = useState<'form' | 'historico'>('form')

    const [orgao, setOrgao] = useState('')
    const [processo, setProcesso] = useState('')
    const [dataDocumento, setDataDocumento] = useState('')
    const [objeto, setObjeto] = useState('')
    const [fornecedorNome, setFornecedorNome] = useState('')
    const [cnpj, setCnpj] = useState('')
    const [hipotese, setHipotese] = useState(HIPOTESES[0])
    const [precoProposto, setPrecoProposto] = useState('')
    const [referencias, setReferencias] = useState<ReferenciaInex[]>([])
    const [fundamentacao, setFundamentacao] = useState('')
    const [responsavel, setResponsavel] = useState('')

    const [buscando, setBuscando] = useState(false)
    const [ocupado, setOcupado] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)
    const [historico, setHistorico] = useState<any[]>([])
    const [iaBusy, setIaBusy] = useState<string | null>(null)

    const assistir = async (
        campo: string,
        dados: Record<string, any>,
        aplicar: (texto: string) => void
    ) => {
        setMsg(null)
        setIaBusy(campo)
        try {
            const r = await api.assistente(campo, dados)
            if (r.erro) setMsg(r.erro)
            else if (r.texto) aplicar(r.texto)
            else setMsg('A Licito.Guru IA não retornou conteúdo.')
        } catch (e) {
            console.error(e)
            setMsg('Não foi possível gerar com a Licito.Guru IA agora.')
        } finally {
            setIaBusy(null)
        }
    }

    const dados = (): DadosInexigibilidade => ({
        orgao,
        processo,
        data_documento: dataDocumento || undefined,
        objeto,
        fornecedor_nome: fornecedorNome,
        cnpj,
        hipotese,
        preco_proposto: Number(precoProposto) || undefined,
        referencias,
        fundamentacao: fundamentacao || undefined,
        responsavel: responsavel || undefined,
    })

    const par = parecer(referencias, Number(precoProposto))

    const buscarPncp = async () => {
        if (fornecedorNome.trim().length < 3 && cnpj.replace(/\D/g, '').length < 11) {
            setMsg('Informe o nome do fornecedor (mín. 3 letras) ou o CNPJ para buscar no PNCP.')
            return
        }
        setMsg(null)
        setBuscando(true)
        try {
            const r = await api.buscarPncp(fornecedorNome, cnpj)
            if (r.erro) setMsg(r.erro)
            else if (!r.total) setMsg('Nenhum contrato do fornecedor encontrado no PNCP.')
            else setReferencias((prev) => [...prev, ...r.referencias])
        } catch (e) {
            console.error(e)
            setMsg('Falha ao buscar contratos no PNCP.')
        } finally {
            setBuscando(false)
        }
    }

    const setRef = (i: number, campo: keyof ReferenciaInex, valor: any) =>
        setReferencias((prev) => prev.map((r, j) => (j === i ? {...r, [campo]: valor} : r)))
    const addRef = () =>
        setReferencias((prev) => [...prev, {contratante: '', natureza: 'Público', data: '', valor: undefined, fonte: ''}])
    const rmRef = (i: number) => setReferencias((prev) => prev.filter((_, j) => j !== i))

    const acao = async (fn: () => Promise<void>, sucesso?: string) => {
        setOcupado(true)
        setMsg(null)
        try {
            await fn()
            if (sucesso) setMsg(sucesso)
        } catch (e) {
            console.error(e)
            setMsg('Não foi possível concluir a ação.')
        } finally {
            setOcupado(false)
        }
    }

    const carregarHistorico = useCallback(async () => {
        try {
            const r = await api.listar()
            setHistorico(r.itens || [])
        } catch (e) {
            console.error(e)
        }
    }, [api])

    useEffect(() => {
        if (view === 'historico') carregarHistorico()
    }, [view, carregarHistorico])

    // Pré-preenche órgão e responsável com os dados do usuário logado (quando carregarem).
    useEffect(() => {
        setOrgao((o) => o || orgaoUsuario)
        setResponsavel((r) => r || nomeUsuario)
    }, [orgaoUsuario, nomeUsuario])

    return (
        <Container>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5}}>
                <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a'}}>
                    Contratação direta — Justificativa de Preço
                </Typography>
                <Button
                    onClick={() => setView(view === 'form' ? 'historico' : 'form')}
                    sx={{textTransform: 'none', fontWeight: 600, color: ACCENT}}
                >
                    {view === 'form' ? 'Ver histórico' : '← Voltar ao formulário'}
                </Button>
            </Box>
            <Typography variant="body2" sx={{color: '#5b6b7b', mb: 3}}>
                Inexigibilidade de licitação (art. 74 da Lei nº 14.133/2021). Como não há competição, a
                justificativa de preço (art. 72, VII) confronta o valor proposto com os preços praticados
                pelo próprio fornecedor em outras contratações.
            </Typography>

            {msg && (
                <Alert severity="info" sx={{mb: 2}} onClose={() => setMsg(null)}>
                    {msg}
                </Alert>
            )}

            {view === 'historico' ? (
                <Card>
                    <SectionTitle>Histórico de justificativas</SectionTitle>
                    {historico.length === 0 ? (
                        <Typography variant="body2" sx={{color: '#7a8896'}}>
                            Nenhuma justificativa salva ainda.
                        </Typography>
                    ) : (
                        <Box sx={{overflowX: 'auto'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr>
                                        <Th>Data</Th>
                                        <Th>Fornecedor</Th>
                                        <Th>Órgão</Th>
                                        <Th>Objeto</Th>
                                        <Th>Preço</Th>
                                        <Th>Compatibilidade</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historico.map((h) => (
                                        <tr key={h.id}>
                                            <Td>{h.created_at ? new Date(h.created_at).toLocaleString('pt-BR') : '—'}</Td>
                                            <Td>{h.fornecedor_nome}</Td>
                                            <Td>{h.orgao}</Td>
                                            <Td>{(h.objeto || '').slice(0, 60)}</Td>
                                            <Td>{brl(h.preco_proposto)}</Td>
                                            <Td>{h.situacao}</Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Card>
            ) : (
                <>
                    <Card>
                        <SectionTitle>Identificação</SectionTitle>
                        <FieldLabel>Órgão/entidade</FieldLabel>
                        <TextField size="small" fullWidth value={orgao} onChange={(e) => setOrgao(e.target.value)} placeholder="Ex.: Câmara Municipal de ..." />
                        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                            <Box sx={{flex: 1, minWidth: 200}}>
                                <FieldLabel>Processo administrativo nº</FieldLabel>
                                <TextField size="small" fullWidth value={processo} onChange={(e) => setProcesso(e.target.value)} placeholder="Ex.: 04/2025" />
                            </Box>
                            <Box sx={{flex: 1, minWidth: 200}}>
                                <FieldLabel>Data do documento</FieldLabel>
                                <TextField size="small" fullWidth value={dataDocumento} onChange={(e) => setDataDocumento(e.target.value)} placeholder="dd/mm/aaaa (em branco = hoje)" />
                            </Box>
                        </Box>
                        <FieldLabel>Objeto</FieldLabel>
                        <TextField size="small" fullWidth multiline minRows={2} value={objeto} onChange={(e) => setObjeto(e.target.value)} placeholder="Descrição do objeto a ser contratado" />
                        <Button
                            size="small"
                            disabled={iaBusy !== null}
                            startIcon={iaBusy === 'objeto' ? <CircularProgress size={14} /> : undefined}
                            onClick={() => assistir('objeto', {texto: objeto}, setObjeto)}
                            sx={{textTransform: 'none', mt: 0.5, color: ACCENT, fontWeight: 600}}
                        >
                            ✨ Descrever objeto com Licito.Guru IA
                        </Button>
                    </Card>

                    <Card>
                        <SectionTitle>Fornecedor e fundamento</SectionTitle>
                        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                            <Box sx={{flex: 2, minWidth: 220}}>
                                <FieldLabel>Fornecedor (razão social)</FieldLabel>
                                <TextField size="small" fullWidth value={fornecedorNome} onChange={(e) => setFornecedorNome(e.target.value)} />
                            </Box>
                            <Box sx={{flex: 1, minWidth: 160}}>
                                <FieldLabel>CNPJ</FieldLabel>
                                <TextField size="small" fullWidth value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0000-00" />
                            </Box>
                        </Box>
                        <Box sx={{mt: 2, p: 2.2, borderRadius: '14px', background: 'linear-gradient(135deg,#eaf2ff,#f7faff)', border: `1px solid ${ACCENT}40`}}>
                            <Typography sx={{fontWeight: 800, color: '#1f2b3a', mb: 0.3, fontSize: '15px'}}>
                                🔎 Busca automática no PNCP
                            </Typography>
                            <Typography variant="body2" sx={{color: '#5b6b7b', mb: 1.5}}>
                                Encontramos os contratos do próprio fornecedor no Portal Nacional de Contratações
                                Públicas e trazemos os preços praticados — a base da justificativa de preço na
                                inexigibilidade (art. 72, VII).
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                disabled={buscando}
                                onClick={buscarPncp}
                                startIcon={buscando ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                                sx={{textTransform: 'none', fontWeight: 700, backgroundColor: ACCENT, '&:hover': {backgroundColor: ACCENT}, px: 3, py: 1.25, boxShadow: `0 4px 14px ${ACCENT}55`}}
                            >
                                {buscando ? 'Buscando no PNCP…' : 'Buscar contratos no PNCP'}
                            </Button>
                        </Box>
                        <FieldLabel>Hipótese de inexigibilidade (art. 74)</FieldLabel>
                        <TextField select size="small" fullWidth value={hipotese} onChange={(e) => setHipotese(e.target.value)}>
                            {HIPOTESES.map((h) => (
                                <MenuItem key={h} value={h} sx={{whiteSpace: 'normal'}}>
                                    {h}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FieldLabel>Preço proposto / a contratar (R$)</FieldLabel>
                        <TextField size="small" type="number" value={precoProposto} onChange={(e) => setPrecoProposto(e.target.value)} onWheel={(e) => (e.target as HTMLElement).blur()} placeholder="Ex.: 12000.00" sx={{maxWidth: 240}} />
                    </Card>

                    <Card>
                        <SectionTitle>Preços praticados pelo fornecedor (referências)</SectionTitle>
                        <Box sx={{overflowX: 'auto'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr>
                                        <Th>Contratante</Th>
                                        <Th>Natureza</Th>
                                        <Th>Data</Th>
                                        <Th>Valor (R$)</Th>
                                        <Th>Fonte</Th>
                                        <Th></Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referencias.map((r, i) => (
                                        <tr key={i}>
                                            <Td>
                                                <TextField size="small" fullWidth value={r.contratante || ''} onChange={(e) => setRef(i, 'contratante', e.target.value)} />
                                                {r.objeto ? <small style={{color: '#94a3b8'}}>{r.objeto.slice(0, 80)}</small> : null}
                                            </Td>
                                            <Td>
                                                <TextField select size="small" value={r.natureza || 'Público'} onChange={(e) => setRef(i, 'natureza', e.target.value)} sx={{width: 110}}>
                                                    <MenuItem value="Público">Público</MenuItem>
                                                    <MenuItem value="Privado">Privado</MenuItem>
                                                </TextField>
                                            </Td>
                                            <Td><TextField size="small" value={r.data || ''} onChange={(e) => setRef(i, 'data', e.target.value)} sx={{width: 110}} /></Td>
                                            <Td><TextField size="small" type="number" value={r.valor ?? ''} onChange={(e) => setRef(i, 'valor', Number(e.target.value))} onWheel={(e) => (e.target as HTMLElement).blur()} sx={{width: 120}} /></Td>
                                            <Td>
                                                <TextField size="small" value={r.fonte || ''} onChange={(e) => setRef(i, 'fonte', e.target.value)} sx={{width: 140}} />
                                                {r.link ? (
                                                    <a href={r.link} target="_blank" rel="noopener noreferrer" style={{display: 'inline-flex', alignItems: 'center', gap: 3, color: ACCENT, fontSize: 12, marginTop: 5, textDecoration: 'none'}}>
                                                        Ver no PNCP <OpenInNewIcon sx={{fontSize: 13}} />
                                                    </a>
                                                ) : null}
                                            </Td>
                                            <Td>
                                                <Tooltip title="Remover">
                                                    <IconButton size="small" onClick={() => rmRef(i)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                        <Button startIcon={<AddIcon />} onClick={addRef} sx={{textTransform: 'none', mt: 1}}>
                            Adicionar referência
                        </Button>
                        <Box sx={{mt: 2, p: 2, borderRadius: '12px', background: '#f1f5f9'}}>
                            <Typography variant="caption" sx={{fontWeight: 700, color: '#475569'}}>
                                Análise de compatibilidade
                            </Typography>
                            <Typography variant="body2" sx={{color: par.cor, fontWeight: 500}}>
                                {par.texto}
                            </Typography>
                        </Box>
                    </Card>

                    <Card>
                        <SectionTitle>Fundamentação e responsável</SectionTitle>
                        <FieldLabel>Fundamentação (opcional)</FieldLabel>
                        <TextField size="small" fullWidth multiline minRows={3} value={fundamentacao} onChange={(e) => setFundamentacao(e.target.value)} />
                        <Button
                            size="small"
                            disabled={iaBusy !== null}
                            startIcon={iaBusy === 'fundamentacao_inexigibilidade' ? <CircularProgress size={14} /> : undefined}
                            onClick={() =>
                                assistir(
                                    'fundamentacao_inexigibilidade',
                                    {
                                        orgao,
                                        objeto,
                                        fornecedor_nome: fornecedorNome,
                                        cnpj,
                                        hipotese,
                                        preco_proposto: Number(precoProposto) || undefined,
                                        referencias,
                                    },
                                    setFundamentacao
                                )
                            }
                            sx={{textTransform: 'none', mt: 0.5, color: ACCENT, fontWeight: 600}}
                        >
                            ✨ Redigir fundamentação com Licito.Guru IA
                        </Button>
                        <FieldLabel>Responsável pela pesquisa</FieldLabel>
                        <TextField size="small" fullWidth value={responsavel} onChange={(e) => setResponsavel(e.target.value)} />
                    </Card>

                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                        <Button variant="contained" disabled={ocupado} onClick={() => acao(() => api.baixarPdf(dados()))} sx={{textTransform: 'none', fontWeight: 600, backgroundColor: ACCENT, '&:hover': {backgroundColor: ACCENT}}}>
                            Gerar PDF
                        </Button>
                        <Button variant="outlined" disabled={ocupado} onClick={() => acao(() => api.verDocumento(dados()))} sx={{textTransform: 'none'}}>
                            Ver (HTML)
                        </Button>
                        <Button variant="outlined" disabled={ocupado} onClick={() => acao(() => api.baixarWord(dados()))} sx={{textTransform: 'none'}}>
                            Gerar Word
                        </Button>
                        <Button variant="outlined" disabled={ocupado} onClick={() => acao(async () => { await api.salvar(dados()) }, 'Justificativa salva no histórico.')} sx={{textTransform: 'none'}}>
                            Salvar no histórico
                        </Button>
                    </Box>
                </>
            )}
        </Container>
    )
}
